<script setup>
import { computed } from "vue"
import { usePage } from "@inertiajs/vue3"
import Icon from "@/components/feedback/Icon.vue"

const props = defineProps({
  variant: {
    type: String,
    default: "info",
    validator: (v) => ["success", "danger", "warning", "info"].includes(v),
  },
  message: { type: String, required: true },
})

defineEmits(["dismiss"])

const ui = usePage().props.ui ?? {}
const dismissLabel = computed(() => ui.dismiss ?? "Dismiss")

const variantConfig = {
  success: {
    icon: "check-good",
    role: "status",
    aria: "polite",
    iconBg: "bg-emerald-100 text-emerald-700",
  },
  danger: {
    icon: "alert-cirlcle-error",
    role: "alert",
    aria: "assertive",
    iconBg: "bg-rose-100 text-rose-700",
  },
  warning: {
    icon: "alert-error",
    role: "status",
    aria: "polite",
    iconBg: "bg-amber-100 text-amber-700",
  },
  info: {
    icon: "info",
    role: "status",
    aria: "polite",
    iconBg: "bg-secondary/15 text-primary",
  },
}

const cfg = computed(() => variantConfig[props.variant])
</script>

<template>
  <div
    :role="cfg.role"
    :aria-live="cfg.aria"
    class="pointer-events-auto flex items-start gap-3 w-[28rem] max-w-[calc(100vw-2rem)] rounded-xl bg-white shadow-lg ring-1 ring-black/5 p-4"
  >
    <div
      class="flex items-center justify-center w-8 h-8 rounded-full shrink-0"
      :class="cfg.iconBg"
    >
      <Icon :name="cfg.icon" :size="18" />
    </div>

    <p class="flex-1 text-sm text-black/80 leading-relaxed pt-1">
      {{ message }}
    </p>

    <button
      type="button"
      class="self-center inline-flex items-center justify-center shrink-0 -m-1 p-1 rounded-md text-black/40 hover:text-black hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-secondary"
      :aria-label="dismissLabel"
      @click="$emit('dismiss')"
    >
      <Icon name="x-close-delete" :size="16" />
    </button>
  </div>
</template>
